import { html, TemplateResult, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import globalStyles from './styles/outline-heading.global.pcss?inline';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';

export const allowedHeadingWeights = ['regular','semibold', 'bold'] as const;
export type HeadingWeight = (typeof allowedHeadingWeights)[number];
export const allowedHeadingSizes = ['xl', 'lg', 'md', 'sm', 'xs', '2xs', '3xs', 'eyebrow'] as const;
export type HeadingSize = (typeof allowedHeadingSizes)[number];
export const allowedHeadingFonts = ['heading', 'body'] as const;
export type HeadingFont = (typeof allowedHeadingFonts)[number];
/**
 * The element name, reused throughout the codebase
 * @type {string}
 */
const componentName = 'outline-heading';

/**
 * The Outline Core Heading component
 *
 * A reusable LitElement component for headings.
 * Heading styles are intentionally tag-agnostic, so any heading element can
 * have any level style.
 *
 * @element outline-heading
 * @extends LitElement
 * @slot - Placeholder for text to be inserted into the component
 * @property heading-size
 *
 */
@customElement(componentName)
export class OutlineHeading extends LitElement {
  /**
   * Adopted stylesheets for the component
   *
   * @type {AdoptedStyleSheets}
   */
  adoptedStyleSheets = new AdoptedStyleSheets(this, {
    globalCSS: globalStyles,
    // encapsulatedCSS: encapsulatedStyles,
  });
    /**
     * The size of the heading text.
     */
    @property({ type: String, attribute: 'heading-size' }) 
    size: HeadingSize = 'lg';

   /**
     * Font weight 'regular', 'semibold', 'bold'.
     */
   @property({ type: String, reflect: true, attribute: 'weight' })
   weight: HeadingWeight = 'bold';

   /**
    * If font is italicized
    */
   @property({type: Boolean, reflect: true })
   italic: boolean;
   
   /**
    * Font family
    */
   @property({type: String, reflect: true })
   family: HeadingFont;


  /**
   * Render method to define the component's template
   *
   * @returns {TemplateResult} A lit-html TemplateResult object representing the component's template
   */
  render(): TemplateResult {
    return html`<slot></slot>`;
  }
}

/**
 * Declare the component in the global HTMLElementTagNameMap
 */
declare global {
  interface HTMLElementTagNameMap {
    [componentName]: OutlineHeading;
  }
}
